/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module SecurityModule
 */

import {
    Component,
    Input,
} from '@angular/core';

import { L10nService } from '@vmw/ngx-vip';
import { SchemaService } from 'ajs/modules/core/services/schema-service';
import { IAviDropdownOption } from 'standalone-components/avi-dropdown';
import { SSLCertificateConfigItem } from 'ajs/modules/security';
import { createOptionsFromEnumProps } from 'ng/shared/utils/dropdown.utils';
import { SSLKeyAlgorithm } from 'generated-types';
import { SSLKeyMldsaParams } from 'object-types';

import * as l10n from './ssl-certificate-imported-information.l10n';

const { ENGLISH: dictionary, ...l10nKeys } = l10n;

/**
 * @description Component for displaying imported information
 *
 * @author Nisar Nadaf
 */
@Component({
    selector: 'ssl-certificate-imported-information',
    templateUrl: './ssl-certificate-imported-information.component.html',
})
export class SSLCertificateImportedInformationComponent {
    /**
     * SSLCertificateConfigItem instance.
     */
    @Input()
    public certificate: SSLCertificateConfigItem;

    /**
     * Get keys from source bundles for template usage
     */
    public readonly l10nKeys = l10nKeys;

    /**
     * SSLKeyAlgorithm dropdown options.
     */
    public sslKeyAlgorithmOptions: IAviDropdownOption[];

    /**
     * SSLRSAKeySize dropdown options.
     */
    public sslRsaKeySizeOptions: IAviDropdownOption[];

    /**
     * SSLRSAKeyECCurve dropdown options.
     */
    public sslKeyECCurveOptions: IAviDropdownOption[];

    /**
     * SSLKeyMldsaAlgorithm dropdown options.
     */
    public sslKeyMldsaAlgorithmOptions: IAviDropdownOption[];

    /**
     * Hash of SSLKeyAlgorithm type enum
     */
    public readonly sslKeyAlgorithmTypeHash = {
        SSL_KEY_ALGORITHM_EC: SSLKeyAlgorithm.SSL_KEY_ALGORITHM_EC,
        SSL_KEY_ALGORITHM_RSA: SSLKeyAlgorithm.SSL_KEY_ALGORITHM_RSA,
        SSL_KEY_ALGORITHM_MLDSA: SSLKeyAlgorithm.SSL_KEY_ALGORITHM_MLDSA,
    };

    /**
     * SSLKeyMldsaParams object type for template usage.
     */
    public readonly SSLKeyMldsaParams = SSLKeyMldsaParams;

    constructor(
        l10nService: L10nService,
        schemaService: SchemaService,
    ) {
        l10nService.registerSourceBundles(dictionary);

        const sslKeyAlgorithmEnumValues = schemaService.getEnumValues('SSLKeyAlgorithm');
        const sslRSAKeySizeEnumValues = schemaService.getEnumValues('SSLRSAKeySize');
        const sslKeyECCurveEnumValues = schemaService.getEnumValues('SSLKeyECCurve');
        const sslKeyMldsaAlgorithmEnumValues = schemaService.getEnumValues('SSLKeyMldsaAlgorithm');

        this.sslKeyAlgorithmOptions = createOptionsFromEnumProps(sslKeyAlgorithmEnumValues);
        this.sslRsaKeySizeOptions = createOptionsFromEnumProps(sslRSAKeySizeEnumValues);
        this.sslKeyECCurveOptions = createOptionsFromEnumProps(sslKeyECCurveEnumValues);
        this.sslKeyMldsaAlgorithmOptions = createOptionsFromEnumProps(sslKeyMldsaAlgorithmEnumValues);
    }
}
